"use client";

/**
 * <TrackedLink> — a next/link that fires an explicit PostHog event on click
 * before navigating. Used for the conversion-funnel + booking CTAs so the
 * funnel doesn't rely on autocapture (spec §4/§5).
 *
 * Safe by construction: capture is wrapped in try/catch and is a no-op when
 * PostHog isn't initialized (e.g. NEXT_PUBLIC_POSTHOG_KEY unset), so a tracking
 * failure can never block navigation or rendering. Only non-sensitive
 * `eventProps` should be passed (plan / cta_location / page_path).
 */

import * as React from "react";
import Link from "next/link";
import posthog from "posthog-js";
import type { AnalyticsEvent, SafeEventProps } from "@/lib/analytics/events";

type TrackedLinkProps = React.ComponentProps<typeof Link> & {
  event: AnalyticsEvent;
  eventProps?: SafeEventProps;
};

export function TrackedLink({
  event,
  eventProps,
  onClick,
  ...rest
}: TrackedLinkProps) {
  return (
    <Link
      {...rest}
      onClick={(e) => {
        try {
          posthog.capture(event, eventProps);
        } catch {
          /* analytics must never break navigation */
        }
        onClick?.(e);
      }}
    />
  );
}
